import { useLayoutEffect, useRef, useState } from "react";
import { Frame, PencilRuler } from "lucide-react";

/** What a touch on a wall should do while the confirm pill is in play. */
export function wallTapIntent(
  armedKey: string | null,
  tappedKey: string,
  confirm: boolean,
): "open" | "keep" | "arm" {
  if (!confirm) return "open";
  // A second tap on the same wall keeps the pill up. It does not pick a side.
  if (armedKey === tappedKey) return "keep";
  return "arm";
}

/** Keep a centered chip fully on screen, 8px in from the canvas edges. */
export function clampChipCenter(anchorX: number, canvasWidth: number, chipWidth: number): number {
  const half = chipWidth / 2;
  const min = half + 8;
  const max = Math.max(min, canvasWidth - half - 8);
  return Math.min(Math.max(anchorX, min), max);
}

/**
 * Two equal actions for a wall highlighted in Design. Move changes the
 * room's shape; add opens the elevation for doors, windows, and art.
 */
export function WallActionChip({
  x,
  y,
  canvasWidth,
  onMoveWall,
  onAddToWall,
}: {
  x: number;
  y: number;
  canvasWidth: number;
  onMoveWall: () => void;
  onAddToWall: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    const next = ref.current?.offsetWidth ?? 0;
    if (next) setWidth(next);
  }, []);

  const left = width > 0 ? clampChipCenter(x, canvasWidth, width) : x;

  return (
    <div
      ref={ref}
      role="group"
      aria-label="Wall actions"
      data-testid="wall-action-chip"
      className="absolute z-20 inline-flex h-11 -translate-x-1/2 -translate-y-[calc(100%+1.125rem)] items-stretch overflow-hidden whitespace-nowrap rounded-full bg-ink-900 text-body-sm font-semibold text-white shadow-lg shadow-ink-900/25"
      style={{ left, top: y }}
    >
      <button
        type="button"
        onClick={onMoveWall}
        className="inline-flex h-11 items-center gap-1.5 pl-3.5 pr-3 active:bg-ink-800"
      >
        <PencilRuler className="size-4" aria-hidden />
        Move wall
      </button>
      <span className="w-px self-stretch bg-white/20" aria-hidden />
      <button
        type="button"
        onClick={onAddToWall}
        className="inline-flex h-11 items-center gap-1.5 pl-3 pr-3.5 active:bg-ink-800"
      >
        <Frame className="size-4" aria-hidden />
        Add doors & art
      </button>
    </div>
  );
}
